import React from 'react';
/**
 * DjayAvatar part — treatment finishes (the cosmetic "rarity finish" axis).
 *
 * Treatments are ORTHOGONAL to variant/accessory: they layer aura / glow /
 * gummy inner-light / sparkle on top of ANY skin without touching identity
 * (DJAY_MASCOT_DOMAIN_2026 §Treatments). Inspired by — not copied from — the
 * Fortnite.GG locker (translucent gummy body, inner glow, drifting particles).
 *
 * Renders in two passes so it can sit on both sides of the body z-stack:
 *   layer="under" → behind the body (aura halo)
 *   layer="over"  → in front of the body (inner glow, sparkle particles)
 *
 * The radial-glow gradient + soft-blur filter <defs> live in the orchestrator
 * (DjayAvatarView) keyed off `uid`, so this part only references them.
 */
import type { DjayTreatment, VariantColorScheme } from '../types';
interface TreatmentProps {
  treatment: DjayTreatment;
  layer: 'under' | 'over';
  uid: string;
  colors: VariantColorScheme;
  showDetails: boolean;
}
/** Deterministic-ish particle positions above the head (viewBox 0 0 200 220). */
const SPARKLE_BITS = [{
  cx: 100,
  cy: 16,
  r: 2.4,
  delay: '0s'
}, {
  cx: 78,
  cy: 30,
  r: 1.8,
  delay: '0.6s'
}, {
  cx: 124,
  cy: 26,
  r: 2,
  delay: '1.1s'
}, {
  cx: 64,
  cy: 46,
  r: 1.4,
  delay: '1.6s'
}, {
  cx: 138,
  cy: 44,
  r: 1.6,
  delay: '0.3s'
}, {
  cx: 110,
  cy: 8,
  r: 1.6,
  delay: '1.4s'
}];
function Diamond({
  cx,
  cy,
  r,
  fill
}: {
  cx: number;
  cy: number;
  r: number;
  fill: string;
}) {
  return <path d={`M${cx} ${cy - r} L${cx + r} ${cy} L${cx} ${cy + r} L${cx - r} ${cy} Z`} fill={fill} data-id="element-3527" data-mp="2aze53rik1au" />;
}
export function Treatment({
  treatment,
  layer,
  uid,
  colors,
  showDetails
}: TreatmentProps) {
  if (treatment === 'none') return null;
  // Particles + gummy detail only read well at larger sizes.
  const accent = colors.treatmentAccent;
  // ── UNDER layer (behind body) ──────────────────────────────────────
  if (layer === 'under') {
    if (treatment === 'aura') {
      return <>
          {/* Slow counter-rotating energy ring sits behind the bloom for a
                           richer, layered halo. Separate <g> so it can spin independently
                           of the breathing bloom (both disabled under reduced-motion). */}
          <g className="djay-aura-ring" aria-hidden="true" data-id="element-3528" data-mp="3nrsidfc6rmzw">
            <circle cx="100" cy="120" r="86" fill="none" stroke={accent} strokeOpacity="0.5" strokeWidth="2.5" strokeDasharray="14 22" strokeLinecap="round" data-id="element-3529" data-mp="3sl3tvtry0ry5" />
            
          </g>
          <g className="djay-aura" aria-hidden="true" data-id="element-3530" data-mp="6cc8n1za4c0v">
            <circle cx="100" cy="120" r="92" fill={`url(#${uid}-treatment-radial)`} data-id="element-3531" data-mp="6eyix9dk15ms" />
            
            <circle cx="100" cy="120" r="68" fill={`url(#${uid}-treatment-radial)`} opacity="0.55" data-id="element-3532" data-mp="acwa2hqvy1st" />
            
            <ellipse cx="100" cy="120" rx="80" ry="86" fill="none" stroke={accent} strokeOpacity="0.45" strokeWidth="2" data-id="element-3533" data-mp="2c6byd7zi34km" />
            
            <ellipse cx="100" cy="120" rx="66" ry="72" fill="none" stroke={accent} strokeOpacity="0.25" strokeWidth="1.5" data-id="element-3534" data-mp="2h682d7876w06" />
            
          </g>
        </>;
    }
    if (treatment === 'glow' || treatment === 'gummy') {
      // A subtle halo so the body reads as lit from within.
      return <g className="djay-treatment-halo" aria-hidden="true" data-id="element-3535" data-mp="1zv2rru2b6r2o">
          <circle cx="100" cy="125" r="80" fill={`url(#${uid}-treatment-radial)`} opacity={treatment === 'gummy' ? 0.9 : 0.55} data-id="element-3536" data-mp="1uwrm7vj1r2q9" />
          
        </g>;
    }
    return null;
  }
  // ── OVER layer (in front of body) ──────────────────────────────────
  return <g aria-hidden="true" data-id="element-3537" data-mp="2nmqdzyomldym">
      {/* Gummy: translucent inner light core + glossy top sheen */}
      {treatment === 'gummy' && <g className="djay-gummy" filter={`url(#${uid}-treatment-soft)`} data-id="element-3538" data-mp="2iof4xq65n7hg">
          <ellipse cx="100" cy="140" rx="40" ry="46" fill={accent} fillOpacity="0.35" data-id="element-3539" data-mp="2ejz0ltc6w84c" />
        
          <ellipse cx="100" cy="155" rx="22" ry="26" fill="#FFFFFF" fillOpacity="0.28" data-id="element-3540" data-mp="177yk1hbtzyn7" />
        
          {showDetails && <path d="M70 70 C84 58, 116 58, 130 70 C116 64, 84 64, 70 70 Z" fill="#FFFFFF" fillOpacity="0.45" data-id="element-3541" data-mp="134acuzr4352h" />}
        </g>}

      {/* Glow + aura: soft inner glow bloom over the body */}
      {(treatment === 'glow' || treatment === 'aura') && <g className="djay-inner-glow" filter={`url(#${uid}-treatment-soft)`} data-id="element-3542" data-mp="2h43v17edlyot">
          <ellipse cx="100" cy="135" rx="36" ry="42" fill={accent} fillOpacity={treatment === 'aura' ? 0.4 : 0.28} data-id="element-3543" data-mp="2h20v9vnf8r6w" />
        
        </g>}

      {/* Sparkle + aura: drifting particle bits above the head */}
      {(treatment === 'sparkle' || treatment === 'aura') && showDetails && <g className="djay-sparkles" data-id="element-3544" data-mp="3erc8cf4841fq">
          {SPARKLE_BITS.map((p, i) => <g key={i} className="djay-sparkle-bit" style={{
        animationDelay: p.delay
      }} data-id="element-3545" data-mp="30suo4rdj172k" data-mpi={i}>
          
              <Diamond cx={p.cx} cy={p.cy} r={p.r * 1.8} fill={accent} data-id="element-3546" data-mp="34sn2jkplq7fu" />
              <Diamond cx={p.cx} cy={p.cy} r={p.r} fill="#FFFFFF" data-id="element-3547" data-mp="1pceh9mg1fyrb" />
            </g>)}
        </g>}
    </g>;
}